import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import { getArticleById, getAllArticles } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));
  const [relatedArticles, setRelatedArticles] = useState<typeof import('@/mocks/categories').ArticleItem[]>([]);

  useEffect(() => {
    if (article) {
      const all = getAllArticles();
      const related = all
        .filter((a) => a.category === article.category && a.id !== article.id)
        .slice(0, 4);
      setRelatedArticles(related);
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }, [id, article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
            <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-950 mb-2">内容不存在</h1>
            <p className="text-sm text-foreground-500 mb-6">该内容可能已被删除或链接无效</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOSchema
        type="Article"
        data={{
          headline: article.title,
          description: article.description,
          author: { '@type': 'Person', name: article.author },
          datePublished: '2025-06-15',
          dateModified: '2025-08-10',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://whtefafuwu.cn/article/${article.id}`,
          },
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://whtefafuwu.cn' },
            { '@type': 'ListItem', position: 2, name: article.category, item: `https://whtefafuwu.cn/category/${article.category}` },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: article.category, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="bg-primary-100 text-primary-600 text-xs font-medium px-2.5 py-1 rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-heart-line"></i> {article.likes}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
              </div>
              <h1 className="font-heading text-xl md:text-3xl text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                {article.description}
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden aspect-video mb-8">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center cursor-pointer hover:bg-white transition-colors hover:scale-110 duration-300">
                  <i className="ri-play-fill text-2xl text-primary-500 ml-0.5"></i>
                </div>
              </div>
              <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                {article.duration}
              </div>
            </div>

            <div className="prose prose-sm md:prose-base max-w-none">
              <p className="text-foreground-700 leading-relaxed">{article.content}</p>
              <p className="text-foreground-700 leading-relaxed mt-4">
                在香蕉视频平台上，每天都有数以万计的优质短视频内容被创作和分享。从搞笑日常到美食制作，从旅行风光到科技数码，
                这里汇聚了来自全国各地创作者的心血与才华。无论你是想看轻松有趣的内容放松心情，还是想学习实用的技能提升自己，
                香蕉视频都能满足你的需求。
              </p>
              <p className="text-foreground-700 leading-relaxed mt-4">
                我们始终相信，每个人都有值得被记录的精彩瞬间。下载香蕉视频App，加入这个充满创意与活力的社区，
                与全球2亿用户一起，发现更多乐趣，分享你的故事。记录生活每一刻，让美好被更多人看见。
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-background-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg text-foreground-950">相关推荐</h3>
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1"
                >
                  查看更多 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block"
                  >
                    <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-2.5">
                      <img
                        src={related.thumbnail}
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-xs px-1 py-0.5 rounded">
                        {related.duration}
                      </div>
                    </div>
                    <h4 className="text-xs font-medium text-foreground-700 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {related.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}